import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, message, Tooltip } from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  SettingOutlined,
  BankOutlined,
  CrownOutlined,
  DollarOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';

const { Header, Sider, Content } = AntLayout;

const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    const cached = (() => {
      try {
        return JSON.parse(localStorage.getItem('super_admin_user') || 'null');
      } catch {
        return null;
      }
    })();
    if (cached) setUser(cached);

    // Verify token + pull authoritative user info from the server.
    authService
      .verifyToken()
      .then((res) => {
        const u = res?.data?.user || res?.user;
        if (u && mounted) {
          setUser(u);
          try {
            localStorage.setItem('super_admin_user', JSON.stringify(u));
          } catch {
            /* ignore quota */
          }
        }
      })
      .catch(() => {
        // If verify fails the ProtectedRoute redirects; nothing to do here.
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch {
      /* swallow — we still log out locally */
    }
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin_user');
    message.success('Logged out');
    navigate('/login');
  };

  const menuItems = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { key: '/users', icon: <TeamOutlined />, label: 'CRM Users', onClick: () => navigate('/users') },
    { key: '/organizations', icon: <BankOutlined />, label: 'Organizations', onClick: () => navigate('/organizations') },
    { key: '/subscription-plans', icon: <CrownOutlined />, label: 'Subscription Plans', onClick: () => navigate('/subscription-plans') },
    { key: '/coupons', icon: <TagsOutlined />, label: 'Coupons & Discounts', onClick: () => navigate('/coupons') },
    { key: '/transactions', icon: <DollarOutlined />, label: 'Transactions', onClick: () => navigate('/transactions') },
    { key: '/billing-config', icon: <DollarOutlined />, label: 'Billing Config', onClick: () => navigate('/billing-config') },
    { key: '/settings', icon: <SettingOutlined />, label: 'Settings', onClick: () => navigate('/settings') },
  ];

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: 'Account', disabled: true },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: handleLogout, danger: true },
  ];

  const sidebarBg = 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 60%, #312e81 100%)';

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={244}
        collapsedWidth={76}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          background: sidebarBg,
          boxShadow: '4px 0 20px -8px rgba(15, 23, 42, 0.4)',
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '0 16px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              color: '#fff',
              fontSize: 14,
              boxShadow: '0 6px 18px -6px rgba(139, 92, 246, 0.6)',
            }}
          >
            QQ
          </div>
          {!collapsed && (
            <div style={{ marginLeft: 12 }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, lineHeight: 1.1 }}>QQCRM</div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5, letterSpacing: 0.5, marginTop: 2 }}>
                SUPER ADMIN
              </div>
            </div>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{
            background: 'transparent',
            borderRight: 0,
            paddingTop: 12,
            fontSize: 13.5,
          }}
        />
      </Sider>

      <AntLayout style={{ marginLeft: collapsed ? 76 : 244, transition: 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'fixed',
            top: 0,
            right: 0,
            left: collapsed ? 76 : 244,
            zIndex: 100,
            transition: 'left 0.2s',
            height: 64,
            boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: 16, width: 40, height: 40 }}
              />
            </Tooltip>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', textTransform: 'capitalize' }}>
              {(location.pathname.replace(/^\//, '').replace(/-/g, ' ') || 'dashboard')}
            </div>
          </div>

          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow trigger={['click']}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '4px 12px 4px 4px',
                borderRadius: 999,
                background: '#f8fafc',
                border: '1px solid #e5e7eb',
                transition: 'background 0.15s ease, border-color 0.15s ease',
              }}
            >
              <Avatar
                size={32}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #ec4899)',
                  color: '#fff',
                  marginRight: 8,
                  fontWeight: 600,
                }}
              >
                {(user?.email || 'S').slice(0, 1).toUpperCase()}
              </Avatar>
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>
                  {user?.email || 'Super Admin'}
                </span>
                <span style={{ fontSize: 10.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {user?.role || 'super_admin'}
                </span>
              </div>
            </div>
          </Dropdown>
        </Header>

        <Content
          style={{
            margin: '88px 24px 24px 24px',
            padding: 24,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            minHeight: 'calc(100vh - 112px)',
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
